// File: whatsapp.service-group-ops.js
import { axiosInstance, DEFAULT_SESSION } from './whatsapp.service-config'; // Assuming DEFAULT_SESSION might be used
import { ERROR_MESSAGES } from './whatsapp.service-errors';
import { _handleError, _extractInviteCode } from './whatsapp.service-utils';
import whatsappConfig from '../config/whatsapp.config'; // Adjust path as needed
import api from '../services/api'; // Import CRM API for lead updates

export const createGroup = async (leadData) => {
  try {
    console.log(`[WhatsApp Group Ops] Creating group for lead:`, JSON.stringify(leadData, null, 2));
    console.log(`[WhatsApp Group Ops] Using WhatsApp API base URL:`, axiosInstance.defaults.baseURL);
    
    // Generate group name
    const groupName = `${whatsappConfig.groupNameTemplate.prefix}${leadData.brideAndGroomName || whatsappConfig.groupNameTemplate.defaultSuffix}`;
    console.log(`[WhatsApp Group Ops] Generated group name: "${groupName}"`);
    
    // Create group request payload
    const createGroupPayload = {
      name: groupName,
      participants: [],
      session: DEFAULT_SESSION
    };
    console.log(`[WhatsApp Group Ops] Group creation payload:`, JSON.stringify(createGroupPayload, null, 2));

    // Create group request
    console.log(`[WhatsApp Group Ops] Making WhatsApp API request to: ${axiosInstance.defaults.baseURL}/${DEFAULT_SESSION}/groups`);
    const createResponse = await axiosInstance.post(`${DEFAULT_SESSION}/groups`, createGroupPayload);

    console.log('[WhatsApp Group Ops] Group created - RESPONSE STATUS:', createResponse.status);
    console.log('[WhatsApp Group Ops] Group created - RESPONSE DATA:', JSON.stringify(createResponse.data, null, 2));
    
    // Extract group ID
    const groupId = createResponse.data.gid?._serialized || '';
    if (!groupId) {
      console.error('[WhatsApp Group Ops] ❌ ERROR: Group ID not found in response');
      throw new Error('Group ID not found in response');
    }

    console.log(`[WhatsApp Group Ops] ✅ Successfully created group with ID: ${groupId}`);
    
    // Set group description
    try {
      console.log('[WhatsApp Group Ops] Setting group description...');
      const descriptionResponse = await axiosInstance.put(
        `${DEFAULT_SESSION}/groups/${groupId}/description`,
        { description: 'www.sumaroh.com' }
      );
      console.log('[WhatsApp Group Ops] ✅ Group description set successfully:', descriptionResponse.status);
    } catch (descriptionError) {
      console.error('[WhatsApp Group Ops] ❌ Failed to set group description:', descriptionError.message);
      console.error('[WhatsApp Group Ops] Error details:', descriptionError.response?.data || 'No response data');
      // Continue despite description error
    }
    
    // Set group picture
    try {
      console.log('[WhatsApp Group Ops] Setting group picture...');
      const pictureResponse = await axiosInstance.put(
        `${DEFAULT_SESSION}/groups/${groupId}/picture`,
        { file: { url: 'https://crm.sumaroh.com/fav.png' } }
      );
      console.log('[WhatsApp Group Ops] ✅ Group picture set successfully:', pictureResponse.status);
    } catch (pictureError) {
      console.error('[WhatsApp Group Ops] ❌ Failed to set group picture:', pictureError.message);
      console.error('[WhatsApp Group Ops] Error details:', pictureError.response?.data || 'No response data');
      // Continue despite picture error
    }
    
    // Prepare lead update
    const leadUpdateData = {
      whatsappGroup: groupId,
      whatsappGroupName: groupName
    };
    console.log(`[WhatsApp Group Ops] Lead update payload:`, JSON.stringify(leadUpdateData, null, 2));
    console.log(`[WhatsApp Group Ops] ✅ Using CRM API client for lead update`);
    
    // Get the CRM API base URL for logging
    const crmApiBaseUrl = api.defaults.baseURL || 'CRM API base URL not available';
    console.log(`[WhatsApp Group Ops] CRM API base URL: ${crmApiBaseUrl}`);
    console.log(`[WhatsApp Group Ops] Lead update endpoint: /api/leads/${leadData.leadId}`);
    
    // Use CRM API for lead update
    try {
      const leadUpdateResponse = await api.put(`/api/leads/${leadData.leadId}`, leadUpdateData);
      console.log('[WhatsApp Group Ops] Lead update response STATUS:', leadUpdateResponse.status);
      console.log('[WhatsApp Group Ops] Lead update response DATA:', JSON.stringify(leadUpdateResponse.data, null, 2));
      console.log('[WhatsApp Group Ops] ✅ Successfully updated lead with WhatsApp group information');
    } catch (updateError) {
      console.error('[WhatsApp Group Ops] ❌ ERROR UPDATING LEAD:', updateError.message);
      console.error('[WhatsApp Group Ops] Error details:', updateError.response?.data || 'No response data');
      console.error('[WhatsApp Group Ops] Check if leadId is valid and CRM API is correctly configured');
    }

    console.log('[WhatsApp Group Ops] Proceeding with participant addition regardless of lead update status');

    // Format participants list
    const leadPhone = leadData.phone?.replace(/\+|\s+/g, '') || '';
    if (!leadPhone) {
      console.warn('[WhatsApp Group Ops] ⚠️ WARNING: Lead phone number is empty or missing');
    } else {
      console.log(`[WhatsApp Group Ops] Formatted lead phone for WhatsApp: ${leadPhone}@c.us`);
    }
    
    console.log(`[WhatsApp Group Ops] Team members to add:`, JSON.stringify(whatsappConfig.teamMembers, null, 2));
    
    // Create the participants array with proper formatting
    const participants = [];
    
    // Add lead phone if available
    if (leadPhone) {
      // Format as expected by WhatsApp API: object with id property
      const formattedLeadPhone = `${leadPhone}@c.us`;
      participants.push({ id: formattedLeadPhone });
      console.log(`[WhatsApp Group Ops] Added lead with phone: ${formattedLeadPhone}`);
    }
    
    // Add team members one by one with proper formatting
    for (const teamMemberPhone of whatsappConfig.teamMembers) {
      // Remove any + signs and spaces to match WhatsApp expected format
      const formattedPhone = teamMemberPhone.replace(/\+|\s+/g, '');
      const formattedTeamMember = `${formattedPhone}@c.us`;
      participants.push({ id: formattedTeamMember });
      console.log(`[WhatsApp Group Ops] Added team member: ${formattedTeamMember}`);
    }
    
    console.log(`[WhatsApp Group Ops] Final participant list (${participants.length} members):`, JSON.stringify(participants, null, 2));

    // Add participants one by one instead of all at once
    console.log(`[WhatsApp Group Ops] Adding participants to group ${groupId} one by one...`);
    
    // Track successful and failed additions
    const participantResults = {
      success: [],
      failed: []
    };
    
    // Add each participant individually
    for (const participant of participants) {
      try {
        // Prepare payload for a single participant
        // The API expects array of objects with 'id' property
        const singleParticipantPayload = {
          participants: [participant] // participant is now an object like { id: "919958455611@c.us" }
          // Note: session is already in the URL, no need to include it in the body
        };
        
        console.log(`[WhatsApp Group Ops] Adding participant: ${participant.id}`);
        
        // Make the API request for this specific participant
        const participantEndpoint = `${DEFAULT_SESSION}/groups/${groupId}/participants/add`;
        const addResponse = await axiosInstance.post(participantEndpoint, singleParticipantPayload);
        
        // Log success and store result
        console.log(`[WhatsApp Group Ops] ✅ Successfully added participant: ${participant.id}`);
        participantResults.success.push({
          id: participant.id,
          status: addResponse.status,
          data: addResponse.data
        });
        
        // Small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 200));
        
      } catch (error) {
        // Log error but continue with next participant
        console.error(`[WhatsApp Group Ops] ❌ Failed to add participant: ${participant.id}`, error.message);
        console.error('[WhatsApp Group Ops] Error details:', JSON.stringify(error.response?.data || 'No response data', null, 2));
        participantResults.failed.push({
          id: participant.id,
          error: error.message,
          details: error.response?.data
        });
      }
    }
    
    // Create a response summary
    const participantResponse = {
      totalParticipants: participants.length,
      addedSuccessfully: participantResults.success.length,
      failed: participantResults.failed.length,
      successDetails: participantResults.success,
      failureDetails: participantResults.failed
    };
    
    // Log the final results
    console.log(`[WhatsApp Group Ops] Participant addition summary:`);
    console.log(`[WhatsApp Group Ops] Total: ${participantResponse.totalParticipants}, Successful: ${participantResponse.addedSuccessfully}, Failed: ${participantResponse.failed}`);
    
    if (participantResponse.failed > 0) {
      console.warn(`[WhatsApp Group Ops] ⚠️ Some participants could not be added, but group was created successfully`);
    } else {
      console.log(`[WhatsApp Group Ops] ✅ All participants added successfully!`);
    }
    
    // Return successful group creation information even if participant addition failed
    console.log('[WhatsApp Group Ops] Returning group creation result');
    return {
      ...createResponse.data,
      addParticipantsResponse: participantResponse,
      groupId,
      groupName
    };
  } catch (error) {
    console.error('[WhatsApp Group Ops] Group creation error:', error);
    console.error('[WhatsApp Group Ops] Error response:', error.response?.data);
    const errorMessage = _handleError(error, ERROR_MESSAGES.GROUP_CREATE_FAILED);
    console.error(`[WhatsApp Group Ops] Group creation failed: ${errorMessage}`);
    throw new Error(errorMessage);
  }
};

export const setGroupPicture = async (groupId, pictureUrl) => {
  try {
    console.log(`[WhatsApp Group Ops] Setting picture for group: ${groupId}`);
    const payload = { url: pictureUrl };

    console.log('[WhatsApp Group Ops] Set group picture payload:', payload);
    const response = await axiosInstance.put(`${DEFAULT_SESSION}/groups/${groupId}/picture`, payload);
    console.log('[WhatsApp Group Ops] Group picture set successfully');
    return response.data;
  } catch (error) {
    console.error('[WhatsApp Group Ops] Setting group picture error:', error);
    console.error('[WhatsApp Group Ops] Error response:', error.response?.data);
    const errorMessage = _handleError(error, ERROR_MESSAGES.GROUP_UPDATE_FAILED);
    console.error(`[WhatsApp Group Ops] Setting group picture failed: ${errorMessage}`);
    throw new Error(errorMessage);
  }
};

export const setGroupName = async (groupId, name) => {
  try {
    console.log(`[WhatsApp Group Ops] Setting name for group: ${groupId} to ${name}`);
    const payload = { name };

    console.log('[WhatsApp Group Ops] Set group name payload:', payload);
    const response = await axiosInstance.put(`${DEFAULT_SESSION}/groups/${groupId}/name`, payload);
    console.log('[WhatsApp Group Ops] Group name set successfully');
    return response.data;
  } catch (error) {
    console.error('[WhatsApp Group Ops] Setting group name error:', error);
    console.error('[WhatsApp Group Ops] Error response:', error.response?.data);
    const errorMessage = _handleError(error, ERROR_MESSAGES.GROUP_UPDATE_FAILED);
    console.error(`[WhatsApp Group Ops] Setting group name failed: ${errorMessage}`);
    throw new Error(errorMessage);
  }
};

export const getGroupInviteInfo = async (inviteLink) => {
  try {
    const inviteCode = _extractInviteCode(inviteLink);
    if (!inviteCode) {
      throw new Error('Invalid invite link format. Expected format: https://chat.whatsapp.com/INVITECODE');
    }

    console.log(`[WhatsApp Group Ops] Getting group info for invite code: ${inviteCode}`);
    const encodedCode = encodeURIComponent(`https://chat.whatsapp.com/${inviteCode}`);
    const url = `${DEFAULT_SESSION}/groups/join-info?code=${encodedCode}`;

    const response = await axiosInstance.get(url);
    console.log('[WhatsApp Group Ops] Group invite info response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[WhatsApp Group Ops] Group invite info error:', error);
    console.error('[WhatsApp Group Ops] Error response:', error.response?.data);
    const errorMessage = _handleError(error, ERROR_MESSAGES.INVITE_INFO_FAILED);
    console.error(`[WhatsApp Group Ops] Group invite info failed: ${errorMessage}`);
    throw new Error(errorMessage);
  }
};

export const joinGroupViaInvite = async (inviteLink) => {
  try {
    const inviteCode = _extractInviteCode(inviteLink);
    if (!inviteCode) {
      throw new Error('Invalid invite link format. Expected format: https://chat.whatsapp.com/INVITECODE');
    }

    console.log(`[WhatsApp Group Ops] Joining group via invite code: ${inviteCode}`);
    const groupInfo = await getGroupInviteInfo(inviteLink); // Call the local module's function

    const response = await axiosInstance.post(`${DEFAULT_SESSION}/groups/join`, {
      code: `https://chat.whatsapp.com/${inviteCode}`
    });

    console.log('[WhatsApp Group Ops] Join group response:', response.data);
    return {
      ...response.data,
      groupInfo
    };
  } catch (error) {
    console.error('[WhatsApp Group Ops] Join group error:', error);
    console.error('[WhatsApp Group Ops] Error response:', error.response?.data);
    const errorMessage = _handleError(error, ERROR_MESSAGES.GROUP_UPDATE_FAILED); // Or a more specific error
    console.error(`[WhatsApp Group Ops] Join group failed: ${errorMessage}`);
    throw new Error(errorMessage);
  }
};